import { supabase } from './supabase'
import type { BrandRole } from './types'

// One read of brand_user_access per load, shared by everyone who needs it.
//
// Resolving "who is this user and what may they see" used to read this table
// three times on a single page load:
//
//   useBrandLoader   brand_id, role, is_owner   (all the user's memberships)
//   useAccountLoader brand_id                   (the same rows, fewer columns)
//   useAccountLoader is_owner for one brand     (a row it had already fetched)
//
// They ran in series — the account loader waits for the brand loader to settle
// — so this was three sequential round trips for one answer. This module reads
// the superset once and hands it to both.
//
// The cache is deliberately shallow: a short TTL that exists only to collapse
// that one burst, plus `force` for every deliberate refresh. It is NOT a
// general-purpose data cache, and nothing that revalidates (tab-return access
// checks, post-add-brand refreshes, entering a brand) may read a cached answer
// — a revoked membership has to be visible immediately.

export interface BrandAccessRow {
  brand_id: string
  role: BrandRole | null
  is_owner: boolean
}

export interface BrandAccessResult {
  rows: BrandAccessRow[]
  /** True when the QUERY failed. Distinct from an empty `rows`, which means the
   *  user genuinely belongs to no brand — the callers route those two to very
   *  different screens (a retry page vs. the signup wizard). */
  failed: boolean
}

/** Long enough to span the brand loader finishing and the account loader
 *  starting; far too short to serve any user-initiated refresh. */
const TTL_MS = 5_000

interface Entry {
  userId: string
  at: number
  promise: Promise<BrandAccessResult>
}

let entry: Entry | null = null

async function fetchRows(userId: string): Promise<BrandAccessResult> {
  const { data, error } = await supabase
    .from('brand_user_access')
    .select('brand_id, role, is_owner')
    .eq('user_id', userId)
    // Owned brands first, then earliest joined — the anchor-brand default.
    .order('is_owner', { ascending: false })
    .order('created_at', { ascending: true })

  if (error) return { rows: [], failed: true }
  return {
    rows: (data ?? []).map(r => ({
      brand_id: r.brand_id as string,
      role: (r.role as BrandRole) ?? null,
      is_owner: Boolean(r.is_owner),
    })),
    failed: false,
  }
}

/**
 * Every brand membership for `userId`, in anchor order.
 *
 * Pass `force` for any refresh that must see current data. A failed read is
 * never cached, so a transient error can't be pinned for the whole TTL.
 */
export function readBrandAccess(
  userId: string,
  opts: { force?: boolean } = {},
): Promise<BrandAccessResult> {
  const now = Date.now()
  if (
    !opts.force &&
    entry &&
    entry.userId === userId &&
    now - entry.at < TTL_MS
  ) {
    return entry.promise
  }
  const promise = fetchRows(userId)
  entry = { userId, at: now, promise }
  // A failure must not be served to the next caller for the rest of the TTL.
  void promise.then(res => {
    if (res.failed && entry?.promise === promise) entry = null
  })
  return promise
}

/** Drop the cached rows — call when membership is known to have changed. */
export function invalidateBrandAccess(): void {
  entry = null
}

/** The user's access row for one brand, or null if they have none. */
export function findAccess(rows: BrandAccessRow[], brandId: string): BrandAccessRow | null {
  return rows.find(r => r.brand_id === brandId) ?? null
}
